import React from 'react'
import './aboutmain.scss'
const AboutMain = () => {
  // Reusable Component for each section block
  const TopicContentBlock = ({ title, children }) => (
    <div className="topicContentBlock">
      <div className="Heading">
        <h3>{title}</h3>
      </div>
      <p>{children}</p>
    </div>
  );

  return (
    <>
      <div className="LeftSide">
        {/* Main Introduction */}
        <TopicContentBlock title="We’ve been building powerful relationships since 1904">
          <p>
            If you’re like us, you don’t want to do business with a faceless institution. You want to know what motivates the people who move a business. You want to know who we are, and what we’re all about. We get that, because it’s how we do business too. We work to get to know our customers and their businesses. Why? Because it helps us provide the best possible insurance solutions.
          </p>
          <p>
            At Sentry—because we are a mutual insurance company—our business is built on a
            foundation of service to our policyholders. Although we’ve grown and changed a lot
            over the past 110 years, one thing hasn’t changed. We still work to serve our
            policyholders, and to help their businesses thrive.
          </p>
          <p>
            Our products and services are made possible by a dedicated team of associates, many of
            whom have been with us for decades. Over 2,000 associates in Stevens Point, Wisconsin—
            and another 2,000 around the country—work each day to help our policyholders prepare
            for the unexpected and plan for the future.
          </p>
          <p>
            Chairman and CEO Pete McPartland joined us in 2010 and since then has been providing
            forward-thinking leadership. Under his direction, we’ve refocused our efforts on
            positioning ourselves as a leader in the business insurance world. We’ve also expanded
            our distribution network by growing with select independent agents.
          </p>
          <p>
            We pride ourselves on providing individualized service to our customers. And one of the ways we do this is by using dedicated teams to serve specific industries. Over the years, departments have grown organically around our products and services.
          </p>
          <p>Today, our focus lies in the following markets:</p>
        </TopicContentBlock>

        {/* Commercial Insurance */}
        <TopicContentBlock title="Commercial insurance">
          <p>
            We offer our business insurance products in specific industries and markets where we
            have the knowledge and ability to deliver the right products to our customers.
          </p>
          <p>
            <b>National Accounts (NA):</b> Think large businesses, with thousands of employees and
            numerous locations. We take a team approach to serving our NA customers, which means
            each customer is served by a dedicated team of associates. Led by Scott Miller, our NA
            team partners with brokers to build relationships with large businesses in diverse
            industries, from health services to retail.
          </p>
          <p>
            <b>Direct Writer:</b> Led by John Hyland, this business unit provides insurance
            solutions to businesses across a range of industries. It’s made up of direct writers
            who work with customers in defined territories.
          </p>

          <p>We specialize in business insurance for the metalworking, manufacturing, plastics, printing, distribution, and food processing industries. You can trace this avenue of the business unit back to the founding of Hardware Dealers Mutual Fire Insurance Company of Wisconsin, our original name, in the early 20th century.</p>
          <p>We also provide property and casualty insurance and safety services to dealerships—including auto, motorcycle, agricultural equipment, and industrial equipment. We’re honored to be endorsed by John Deere® and Harley-Davidson® for our business insurance products.</p>
          <p><b>Transportation:</b> Distributes long-haul truck insurance through a select group of industry-specialized independent agents. Led by Scott Miller, our transportation team protects companies of all sizes, from owner-operators to large fleet operations. In addition to insurance solutions, we offer our trucking customers unique safety programs driven by our certified directors of safety who, on average, bring 20+ years of industry experience to the table.</p>
          <p><b>Regional:</b>Sold solely through appointed independent agents, this part of our business provides insurance to a variety of industries, including commercial construction, commercial real estate, manufacturing, retail trade, service, wholesale trade, and golf courses. Our growing regional business helps support these independent agents by providing local support, underwriting services, safety services, and claims services from eight offices: Irvine, CA; Denver, CO; Windsor, CT; Charlotte, NC; Portland, OR; Dallas, TX; Madison, WI; and Milwaukee, WI.</p>
          <p><b>Hortica®:</b>Since 2015, we’ve been affiliated with Florists’ Mutual Insurance Company. Led by Tim Noble, our Hortica representatives provide insurance solutions to horticultural businesses, including greenhouse growers, nurseries, garden centers, retail florists, wholesale floral distributors, interior plantscapers, sod farms, and lawn care professionals.</p>
          <p><b>Life & Annuities:</b>This is the department that manages our 401(k) plans and business life insurance policies. Led by John Hyland, we serve our 401(k) and life insurance customers with sound investing and exceptional customer service.</p>
          <p><b>Specialty:</b>Our Specialty business unit is led by Heather Schenker – a 30-year veteran in the insurance industry with deep experience in specialty lines management. The Specialty market is often called the “safety valve” of the insurance industry. It encompasses risks that could include the following characteristics:</p>
          <ul>
            <li>Specialized, unique, or niche</li>
            <li>Declined by the standard lines market</li>
            <li>High risk</li>
            <li>In need of large limits</li>
            <li>In markets that are new and emerging in the insurance industry</li>
          </ul>
          <p>To distribute our Specialty products Sentry delegates authority to Program Managers we’ve partnered with who have expertise in a specific market segment or class; they function as an extension of Sentry to provide end-to-end solutions to our shared customers. Specialty operates via two specific Underwriting Companies:</p>
          <ul>
            <li>Point Excess and Surplus Insurance Company, the Sentry paper for all non-admitted Specialty products</li>
            <li>Point Specialty Insurance Company, the Sentry paper for all admitted Specialty products</li>
          </ul>
          <p>Our Specialty business unit will allow us to tailor coverages and terms/conditions more deeply than standard lines while quickly responding to changes in market conditions.</p>
        </TopicContentBlock>

        {/* Personal Insurance */}
        <TopicContentBlock title="Personal insurance">
          <p>
            In addition to our commercial insurance products, we offer personal insurance to select
            consumer markets.
          </p>
          <p>
            <a href="https://www.dairylandinsurance.com/" target="_blank" rel="noopener noreferrer">
              <b>Dairyland</b>
            </a>
            : Our personal lines department provides{" "}
            <a
              href="https://www.dairylandinsurance.com/motorcycle"
              target="_blank"
              rel="noopener noreferrer"
            >
              motorcycle insurance
            </a>
            ,{" "}
            <a
              href="https://www.dairylandinsurance.com/auto"
              target="_blank"
              rel="noopener noreferrer"
            >
              nonstandard auto insurance
            </a>
            , and a range of{" "}
            <a
              href="https://www.dairylandinsurance.com/off-road"
              target="_blank"
              rel="noopener noreferrer"
            >
              off-road insurance
            </a>
            , including ATV/UTV and snowmobile. Led by Pete Anhalt, our Dairyland representatives
            specialize in serving these niche markets.
          </p>
        </TopicContentBlock>

        {/* Hospitality Business */}
        <TopicContentBlock title="Hospitality business">
          <p>
            Since 1982 we’ve operated a destination golf course, event facility, and restaurant.
          </p>
          <p>
            <a href="https://www.sentryworld.com/" target="_blank" rel="noopener noreferrer">
              <b>SentryWorld:</b>
            </a>
            Our world-class 18-hole golf course, banquet facility, restaurant, and sports complex on Sentry’s home office campus. SentryWorld was ranked number 78 by Golf magazine in its Top 100 Courses You Can Play 2016/2017 category. Led by General Manager Mike James, SentryWorld hosts golfers, weddings, and events. In addition, SentryWorld boasts two beautiful restaurants: PJ’s – SentryWorld and Muse at Sentry.
          </p>
          <p>We hope this snapshot of who we are helps you understand what moves our business. If there’s one thing we’ve learned in 120+ years, it’s that the marketplace changes and successful companies change with it. We’ve grown a lot since 1904, and we can help you grow too. Contact us or find an agent to get the conversation started.</p>
        </TopicContentBlock>
      </div>
    </>
  )
}

export default AboutMain